import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Calendar, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import WhatsAppButton from '@/components/layout/WhatsAppButton';
import { contactInfo } from '@/data/mock';
import { sendEmail } from '@/lib/emailService';
import EMAIL_CONFIG from '@/config/emailConfig';
import usePageTitle from '@/hooks/usePageTitle';

function getSettings() {
  try {
    const saved = localStorage.getItem("eliteplus_email_settings");
    if (saved) return { ...EMAIL_CONFIG, ...JSON.parse(saved) };
  } catch {}
  return { ...EMAIL_CONFIG };
}

const ContactPage = () => {
  usePageTitle("Contact Us", "Contact ElitePlus Solutions in UAE for IT consulting, digital transformation, cybersecurity, and ERP advisory services. Book a free strategy call today.");
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [referenceId, setReferenceId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const refId = `ENQ-${new Date().toISOString().slice(0,10).replace(/-/g,'')}-${Math.random().toString(36).substring(2,8).toUpperCase()}`;
    setReferenceId(refId);

    const cfg = getSettings();
    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      company: formData.company || "N/A",
      phone: formData.phone || "N/A",
      service: formData.service || "General Enquiry",
      message: formData.message,
      reference_id: refId,
      submission_date: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
      to_email: cfg.internalToEmail || "info@eliteplus.io",
      reply_to: formData.email,
      company_name: cfg.companyName || "ElitePlus",
      signature_name: cfg.signatureName || "",
      signature_email: cfg.signatureEmail || "",
      signature_phone: cfg.signaturePhone || "",
      website_url: cfg.websiteUrl || "",
    };

    const canSendEmail = cfg.emailEnabled && cfg.EMAILJS_PUBLIC_KEY && cfg.EMAILJS_SERVICE_ID;

    let emailSent = false;
    let emailError = null;

    try {
      if (canSendEmail) {
        // Send visitor acknowledgment
        if (cfg.visitorEmailEnabled && cfg.VISITOR_TEMPLATE_ID) {
          try {
            await sendEmail(cfg.EMAILJS_SERVICE_ID, cfg.VISITOR_TEMPLATE_ID, templateParams, cfg.EMAILJS_PUBLIC_KEY);
            emailSent = true;
          } catch (err) {
            console.error("Visitor email failed:", err);
            emailError = err;
          }
        }

        // Send internal notification
        if (cfg.internalEmailEnabled && cfg.INTERNAL_TEMPLATE_ID) {
          try {
            await sendEmail(cfg.EMAILJS_SERVICE_ID, cfg.INTERNAL_TEMPLATE_ID, templateParams, cfg.EMAILJS_PUBLIC_KEY);
            emailSent = true;
          } catch (err) {
            console.error("Internal email failed:", err);
            if (!emailError) emailError = err;
          }
        }
      } else {
        setError('Email service is not configured. Please contact us directly at info@eliteplus.io');
        setSubmitting(false);
        return;
      }

      if (emailSent) {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setReferenceId('');
          setFormData({ name: '', email: '', company: '', phone: '', service: '', message: '' });
        }, 5000);
      } else {
        const detail = emailError?.message || '';
        setError(`Failed to send your message${detail ? ': ' + detail : ''}. Please try again or contact us directly at info@eliteplus.io`);
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const serviceOptions = [
    'Cybersecurity & Risk Management',
    'Enterprise IT Strategy & Advisory',
    'Enterprise Architecture',
    'Enterprise Software Solutions',
    'Digital Transformation Solutions',
    'Cloud & Infrastructure Solutions',
    'Enterprise AI & Automation',
    'Data Management & Analytics',
    'Software Development & Integration',
    'Managed IT Services & Support',
    'IT Governance & Compliance',
    'IT Project Management',
    'Other'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-600 text-sm font-medium mb-6">
            Contact Us
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 mb-6">
            Let's Start with <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">Clarity</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Complete a short assessment form and book a strategy call with a senior consultant.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="relative pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info Cards */}
            <div className="space-y-6">
              <div data-testid="contact-email-card" className="p-6 bg-white border border-slate-200 rounded-2xl hover:border-cyan-400/50 hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/10 to-teal-500/10 flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-cyan-500" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Email Us</h3>
                <a href={`mailto:${contactInfo.email}`} data-testid="contact-email-link" className="text-slate-500 hover:text-cyan-500 transition-colors duration-300">
                  {contactInfo.email}
                </a>
              </div>

              <div data-testid="contact-phone-card" className="p-6 bg-white border border-slate-200 rounded-2xl hover:border-cyan-400/50 hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/10 to-teal-500/10 flex items-center justify-center mb-4">
                  <Phone className="w-6 h-6 text-cyan-500" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Call Us</h3>
                <a href={`tel:${contactInfo.phone}`} data-testid="contact-phone-link" className="text-slate-500 hover:text-cyan-500 transition-colors duration-300">
                  {contactInfo.phone}
                </a>
              </div>

              <div data-testid="contact-address-card" className="p-6 bg-white border border-slate-200 rounded-2xl hover:border-cyan-400/50 hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/10 to-teal-500/10 flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-cyan-500" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Visit Us</h3>
                <p className="text-slate-500" data-testid="contact-address-text">{contactInfo.address}</p>
              </div>

              <div data-testid="book-call-card" className="p-6 bg-gradient-to-br from-cyan-500/5 to-teal-500/5 border border-cyan-500/20 rounded-2xl">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center mb-4">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Book a Strategy Call</h3>
                <p className="text-slate-500 text-sm mb-4">15-minute discussion with a senior consultant. No obligation.</p>
                <a href="https://calendly.com/umesh-eliteplus/30min" target="_blank" rel="noopener noreferrer">
                  <Button data-testid="schedule-now-btn" className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold">
                    Schedule Now
                  </Button>
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="p-8 bg-white border border-slate-200 rounded-2xl shadow-lg">
                {isSubmitted ? (
                  <div className="text-center py-12" data-testid="form-success-message">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800 mb-4">Message Sent!</h3>
                    <p className="text-slate-500">Thank you for reaching out. Our team will get back to you within 24 hours.</p>
                    {referenceId && (
                      <p className="text-sm text-cyan-600 font-mono mt-3" data-testid="reference-id">Reference: {referenceId}</p>
                    )}
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">Send us a Message</h2>
                    {error && (
                      <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" data-testid="contact-form-error">{error}</div>
                    )}
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-slate-700">Full Name *</Label>
                          <Input id="name" name="name" data-testid="contact-name-input" value={formData.name} onChange={handleChange} required className="bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-cyan-500" placeholder="John Smith" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-slate-700">Email Address *</Label>
                          <Input id="email" name="email" type="email" data-testid="contact-email-input" value={formData.email} onChange={handleChange} required className="bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-cyan-500" placeholder="john@company.com" />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="company" className="text-slate-700">Company</Label>
                          <Input id="company" name="company" data-testid="contact-company-input" value={formData.company} onChange={handleChange} className="bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-cyan-500" placeholder="Your Company" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-slate-700">Phone Number</Label>
                          <Input id="phone" name="phone" type="tel" data-testid="contact-phone-input" value={formData.phone} onChange={handleChange} className="bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-cyan-500" placeholder="+1 (555) 000-0000" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="service" className="text-slate-700">Service of Interest</Label>
                        <select id="service" name="service" data-testid="contact-service-select" value={formData.service} onChange={handleChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:border-cyan-500 transition-colors duration-300">
                          <option value="">Select a service</option>
                          {serviceOptions.map((option, idx) => (
                            <option key={idx} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-slate-700">Message *</Label>
                        <Textarea id="message" name="message" data-testid="contact-message-textarea" value={formData.message} onChange={handleChange} required rows={5} className="bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-cyan-500 resize-none" placeholder="Tell us about your project or challenges..." />
                      </div>
                      <Button type="submit" size="lg" data-testid="contact-submit-btn" disabled={submitting} className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300">
                        <Send className="w-5 h-5 mr-2" />
                        {submitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ContactPage;
